import { defineStore } from 'pinia'

import { ProjectType } from '@/constants/projectTypes'
import { unitPluralLabel, unitSingularLabel } from '@/utils/projectLabels'

export type EditorDetailFamilyKey = 'navigate' | 'write' | 'world' | 'session'

export interface EditorDetailDefinition {
  key: string
  label: string
  icon: string
  family: EditorDetailFamilyKey
  show: (projectType: string | undefined) => boolean
}

export interface EditorDetailFamilyDefinition {
  key: EditorDetailFamilyKey
  label: string
  icon: string
  /** Fixed accent for chrome (not theme tokens — keeps families distinct across themes). */
  accent: string
}

export const editorDetailFamilyDefinitions: EditorDetailFamilyDefinition[] = [
  { key: 'navigate', label: 'Navegar', icon: 'mdi-format-list-numbered', accent: '#26A69A' },
  { key: 'write', label: 'Escribir', icon: 'mdi-pencil-outline', accent: '#42A5F5' },
  { key: 'world', label: 'Mundo', icon: 'mdi-earth', accent: '#66BB6A' },
  { key: 'session', label: 'Sesión', icon: 'mdi-timer-outline', accent: '#FFA726' },
]

export function getFamilyAccent(family: EditorDetailFamilyKey): string {
  return (
    editorDetailFamilyDefinitions.find((f) => f.key === family)?.accent ??
    editorDetailFamilyDefinitions[0].accent
  )
}

export const editorDetailDefinitions: EditorDetailDefinition[] = [
  {
    key: 'chapters',
    label: 'Capítulos',
    icon: 'mdi-format-list-numbered',
    family: 'navigate',
    show: () => true,
  },
  {
    key: 'current',
    label: 'Capítulo actual',
    icon: 'mdi-bookmark-check-outline',
    family: 'navigate',
    show: () => true,
  },
  {
    key: 'format',
    label: 'Formato',
    icon: 'mdi-format-font',
    family: 'write',
    show: (projectType) => projectType !== ProjectType.SCREENPLAY,
  },
  {
    key: 'comments',
    label: 'Comentarios',
    icon: 'mdi-comment-text-outline',
    family: 'write',
    show: () => true,
  },
  {
    key: 'footnotes',
    label: 'Pies de página',
    icon: 'mdi-format-superscript',
    family: 'write',
    show: (projectType) => projectType !== ProjectType.SCREENPLAY,
  },
  {
    key: 'shortcuts',
    label: 'Atajos',
    icon: 'mdi-keyboard-outline',
    family: 'write',
    show: (projectType) => projectType !== ProjectType.SCREENPLAY,
  },
  {
    key: 'todos',
    label: 'Pendientes',
    icon: 'mdi-checkbox-marked-outline',
    family: 'world',
    show: () => true,
  },
  {
    key: 'notes',
    label: 'Notas',
    icon: 'mdi-note-text',
    family: 'world',
    show: () => true,
  },
  {
    key: 'elements',
    label: 'Elementos narrativos',
    icon: 'mdi-card-account-details',
    family: 'world',
    show: () => true,
  },
  {
    key: 'timeline',
    label: 'Cronología',
    icon: 'mdi-timeline',
    family: 'world',
    show: () => true,
  },
  {
    key: 'analysis',
    label: 'Análisis',
    icon: 'mdi-chart-bar',
    family: 'session',
    show: () => true,
  },
  {
    key: 'alerts',
    label: 'Alertas',
    icon: 'mdi-alert-outline',
    family: 'session',
    show: (projectType) => projectType !== ProjectType.SCREENPLAY,
  },
  {
    key: 'sprint',
    label: 'Sprint',
    icon: 'mdi-timer-outline',
    family: 'session',
    show: () => true,
  },
  {
    key: 'concentration',
    label: 'Concentración',
    icon: 'mdi-eye-off-outline',
    family: 'session',
    show: () => true,
  },
]

export interface EditorDetailIconButton {
  key: string
  label: string
  icon: string
}

function detailLabel(
  def: EditorDetailDefinition,
  projectType: string | undefined,
): string {
  if (def.key === 'chapters') {
    return unitPluralLabel(projectType)
  }
  if (def.key === 'current') {
    return `${unitSingularLabel(projectType)} actual`
  }
  return def.label
}

export function resolveAllowedEditorDetail(
  detail: string,
  projectType: string | undefined,
): string {
  const def = editorDetailDefinitions.find((d) => d.key === detail)
  if (def?.show(projectType)) {
    return detail
  }
  const fallback = editorDetailDefinitions.find((d) => d.show(projectType))
  return fallback?.key ?? 'chapters'
}

export function getVisibleEditorDetailButtons(
  projectType: string | undefined,
): EditorDetailIconButton[] {
  return editorDetailDefinitions
    .filter((d) => d.show(projectType))
    .map((d) => ({
      key: d.key,
      icon: d.icon,
      label: detailLabel(d, projectType),
    }))
}

export function getFamilyForDetail(detail: string): EditorDetailFamilyKey {
  const def = editorDetailDefinitions.find((d) => d.key === detail)
  return def?.family ?? 'navigate'
}

export function getVisibleEditorDetailFamilies(
  projectType: string | undefined,
): EditorDetailFamilyDefinition[] {
  return editorDetailFamilyDefinitions.filter((family) =>
    editorDetailDefinitions.some((d) => d.family === family.key && d.show(projectType)),
  )
}

export function getVisibleDetailsForFamily(
  family: EditorDetailFamilyKey,
  projectType: string | undefined,
): EditorDetailIconButton[] {
  return editorDetailDefinitions
    .filter((d) => d.family === family && d.show(projectType))
    .map((d) => ({
      key: d.key,
      icon: d.icon,
      label: detailLabel(d, projectType),
    }))
}

export function resolveDetailForFamily(
  family: EditorDetailFamilyKey,
  projectType: string | undefined,
  lastDetailByFamily: Partial<Record<EditorDetailFamilyKey, string>>,
): string {
  const visible = getVisibleDetailsForFamily(family, projectType)
  if (visible.length === 0) {
    return resolveAllowedEditorDetail('chapters', projectType)
  }

  const remembered = lastDetailByFamily[family]
  if (remembered && visible.some((d) => d.key === remembered)) {
    return remembered
  }

  return visible[0].key
}

export const useRightPanelStore = defineStore('rightPanel', {
  state: () => ({
    selectedDetail: 'chapters',
    show: false,
    lastDetailByFamily: {} as Partial<Record<EditorDetailFamilyKey, string>>,
  }),

  getters: {
    activeFamily(state): EditorDetailFamilyKey {
      return getFamilyForDetail(state.selectedDetail)
    },
  },

  actions: {
    rememberDetail(detail: string) {
      const family = getFamilyForDetail(detail)
      this.lastDetailByFamily[family] = detail
    },

    selectDetail(detail: string, projectType?: string) {
      const resolved = resolveAllowedEditorDetail(detail, projectType)
      this.selectedDetail = resolved
      this.rememberDetail(resolved)
    },

    selectFamily(family: EditorDetailFamilyKey, projectType?: string) {
      const detail = resolveDetailForFamily(family, projectType, this.lastDetailByFamily)
      this.selectDetail(detail, projectType)
    },

    syncDetailFromRoute(detail: string, projectType?: string) {
      const resolved = resolveAllowedEditorDetail(detail, projectType)
      this.selectedDetail = resolved
      this.rememberDetail(resolved)
    },

    showPanel() {
      this.show = true
    },

    hidePanel() {
      this.show = false
    },
  },
})
